import React, { useState, useRef, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { MessageCircle, X, Send, Bot, User, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const renderInlineMarkdown = (text, { isUser } = {}) => {
  if (text == null) return null;
  const str = String(text);
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(str)) !== null) {
    if (match.index > lastIndex) {
      parts.push(str.slice(lastIndex, match.index));
    }
    const token = match[0];
    if (token.startsWith("**") && token.endsWith("**")) {
      parts.push(<strong key={`b-${match.index}`}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`") && token.endsWith("`")) {
      parts.push(
        <code
          key={`c-${match.index}`}
          className={
            isUser
              ? "px-1 py-0.5 rounded bg-white/20 text-white font-mono text-[0.85em]"
              : "px-1 py-0.5 rounded bg-gray-100 text-gray-800 font-mono text-[0.85em]"
          }
        >
          {token.slice(1, -1)}
        </code>
      );
    } else {
      parts.push(token);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < str.length) {
    parts.push(str.slice(lastIndex));
  }
  return parts;
};

const FormattedChatMessage = ({ content, isUser }) => {
  const text = content == null ? "" : String(content);
  const lines = text.split(/\r?\n/);
  const blocks = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // blank line => spacing
    if (!trimmed) {
      blocks.push(<div key={`sp-${i}`} className="h-2" />);
      i += 1;
      continue;
    }

    // fenced code block
    if (trimmed.startsWith("```")) {
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith("```")) {
        codeLines.push(lines[i]);
        i += 1;
      }
      // consume closing fence if present
      if (i < lines.length && lines[i].trim().startsWith("```")) i += 1;

      blocks.push(
        <pre
          key={`pre-${i}`}
          className={
            isUser
              ? "bg-white/15 text-white rounded-md p-2 overflow-x-auto text-xs font-mono"
              : "bg-gray-900 text-gray-100 rounded-md p-2 overflow-x-auto text-xs font-mono"
          }
        >
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // headings (#, ##, ###)
    const headingMatch = trimmed.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const headingText = headingMatch[2];
      blocks.push(
        <div
          key={`h-${i}`}
          className={
            isUser
              ? `text-white font-semibold ${level === 1 ? "text-base" : level === 2 ? "text-sm" : "text-sm"}`
              : `text-gray-900 font-semibold ${level === 1 ? "text-base" : level === 2 ? "text-sm" : "text-sm"}`
          }
        >
          {renderInlineMarkdown(headingText, { isUser })}
        </div>
      );
      i += 1;
      continue;
    }

    // unordered list
    if (/^\s*-\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*-\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ul
          key={`ul-${i}`}
          className={
            isUser
              ? "list-disc pl-5 space-y-1 text-sm text-white"
              : "list-disc pl-5 space-y-1 text-sm text-gray-800"
          }
        >
          {items.map((it, idx) => (
            <li key={`uli-${idx}`}>{renderInlineMarkdown(it, { isUser })}</li>
          ))}
        </ul>
      );
      continue;
    }

    // ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ol
          key={`ol-${i}`}
          className={
            isUser
              ? "list-decimal pl-5 space-y-1 text-sm text-white"
              : "list-decimal pl-5 space-y-1 text-sm text-gray-800"
          }
        >
          {items.map((it, idx) => (
            <li key={`oli-${idx}`}>{renderInlineMarkdown(it, { isUser })}</li>
          ))}
        </ol>
      );
      continue;
    }

    // paragraph: collect consecutive non-special lines
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith("```") &&
      !/^(#{1,3})\s+/.test(lines[i].trim()) &&
      !/^\s*-\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i])
    ) {
      paraLines.push(lines[i]);
      i += 1;
    }
    const para = paraLines.join("\n");
    blocks.push(
      <p
        key={`p-${i}`}
        className={isUser ? "text-sm text-white whitespace-pre-wrap" : "text-sm text-gray-800 whitespace-pre-wrap"}
      >
        {renderInlineMarkdown(para, { isUser })}
      </p>
    );
  }

  return <div className="space-y-2">{blocks}</div>;
};

const ChatbotSection = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && !sessionId) {
      // Generate session ID when chatbot opens
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      loadChatSuggestions();
      
      // Send welcome message
      setTimeout(() => {
        setMessages([{
          type: 'bot',
          message: "Welcome to Gujarat WCD Assistant! I can help you with information about women & child development schemes, nutrition programs, Anganwadi services, and support services. How may I assist you today?",
          timestamp: new Date()
        }]);
      }, 500);
    }
  }, [isOpen, sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadChatSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/chatbot/suggestions`);
      setSuggestions(response.data.suggestions);
    } catch (error) {
      console.error('Error loading chat suggestions:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping || !sessionId) return;

    const userMessage = {
      type: 'user',
      message: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputValue;
    setInputValue("");
    setIsTyping(true);

    try {
      const response = await axios.post(`${API}/chatbot/message`, {
        message: currentInput,
        session_id: sessionId,
        user_ip: "127.0.0.1" // In production, this would be the actual IP
      });

      const botMessage = {
        type: 'bot',
        message: response.data.response,
        timestamp: new Date(response.data.timestamp),
        suggestions: response.data.suggestions
      };
      
      setMessages(prev => [...prev, botMessage]);
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        type: 'bot',
        message: "I'm sorry, I'm experiencing some technical difficulties. Please try again later.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
  };

  const handleQuickActionClick = async (action) => {
    setInputValue(action);
    // Auto-send the message
    setTimeout(() => {
      handleSendMessage();
    }, 100);
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={onToggle}
          className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full w-16 h-16 shadow-2xl hover:shadow-pink-500/25 transition-all duration-300 transform hover:scale-110"
        >
          <MessageCircle className="w-6 h-6" />
        </Button>
        <div className="absolute -top-2 -right-2 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Card className="w-96 h-[32rem] shadow-2xl border-0 overflow-hidden">
        {/* Header */}
        <CardHeader className="bg-gradient-to-r from-pink-500 to-purple-600 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">
                  WCD AI Assistant
                </CardTitle>
                <div className="flex items-center text-sm opacity-90">
                  <div className="w-2 h-2 bg-green-300 rounded-full mr-2 animate-pulse" />
                  Online
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="text-white hover:bg-white/20 p-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        {/* Messages */}
        <CardContent className="flex-1 h-80 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.map((message, index) => (
            <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`flex items-start space-x-2 max-w-xs ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  message.type === 'user' 
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600' 
                    : 'bg-gray-200'
                }`}>
                  {message.type === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Bot className="w-4 h-4 text-gray-600" />
                  )}
                </div>
                <div className={`rounded-lg p-3 ${
                  message.type === 'user' 
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' 
                    : 'bg-white shadow-sm border'
                }`}>
                  <FormattedChatMessage content={message.message} isUser={message.type === 'user'} />
                  <p className={`text-xs mt-1 opacity-70 ${
                    message.type === 'user' ? 'text-white' : 'text-gray-500'
                  }`}>
                    {formatTime(message.timestamp)}
                  </p>
                </div>
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-2 max-w-xs">
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <Bot className="w-4 h-4 text-gray-600" />
                </div>
                <div className="bg-white rounded-lg p-3 shadow-sm border">
                  <div className="flex space-x-1 items-center">
                    <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
                    <span className="text-sm text-gray-600">AI is thinking...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Suggestions */}
          {messages.length > 0 && messages[messages.length - 1].suggestions && !isTyping && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">Quick actions:</p>
              <div className="flex flex-wrap gap-2">
                {messages[messages.length - 1].suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickActionClick(suggestion)}
                    className="text-xs bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-200 text-gray-700 hover:text-orange-600 px-3 py-1 rounded-full transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Initial suggestions when no messages */}
          {messages.length === 1 && suggestions.length > 0 && !isTyping && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">You can ask me about:</p>
              <div className="grid grid-cols-1 gap-2">
                {suggestions.slice(0, 4).map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickActionClick(suggestion)}
                    className="text-left text-xs bg-white hover:bg-pink-50 border border-gray-200 hover:border-pink-200 text-gray-700 hover:text-pink-600 px-3 py-2 rounded-lg transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input */}
        <div className="p-4 border-t bg-white">
          <div className="flex space-x-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask me about WCD schemes, Anganwadi services, or nutrition programs..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
              disabled={isTyping || !sessionId}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isTyping || !sessionId}
              className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white p-2 rounded-lg disabled:opacity-50"
            >
              {isTyping ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ChatbotSection;
